import type { QueryClient, QueryKey } from '@tanstack/react-query'

import type { Catalog } from '@/types'
import type { serializeCatalogBlocks } from '@/utils/catalog.handler'

export type CatalogEditorDraft = {
  baseCatalogId: string
  baseVersion: number
  baseStamp: string
  revision: string
  content: {
    version: string
    blocks: ReturnType<typeof serializeCatalogBlocks>
  }
  date?: string
  supportedLocales: string[]
  defaultLocale: string
  contentLocale: string
  firstPageNumber: number
  rotorFooterLabel?: string
}

export const catalogBaseStamp = (catalog?: Partial<Catalog>) =>
  JSON.stringify([
    catalog?.id,
    catalog?.version,
    catalog?.content,
    catalog?.metadata,
    catalog?.publishDate,
    catalog?.lastUpdated,
  ])

export type CatalogEditorDraftState = {
  drafts: CatalogEditorDraft[]
  lastSaved?: { revision: string; catalogId: string }
}

export const EMPTY_CATALOG_DRAFT_STATE: CatalogEditorDraftState = { drafts: [] }

export const readCatalogDraftState = (client: QueryClient, key: QueryKey) =>
  client.getQueryData<CatalogEditorDraftState>(key) ?? EMPTY_CATALOG_DRAFT_STATE

export const retainCatalogDraft = (
  client: QueryClient,
  key: QueryKey,
  draft: CatalogEditorDraft,
) => {
  // This query has no lookup persistence metadata: it lives only in this session's RAM.
  client.setQueryDefaults(key, { gcTime: Infinity })
  client.setQueryData<CatalogEditorDraftState>(key, (state) => ({
    drafts: [
      ...(state?.drafts ?? []).filter(
        (entry) =>
          entry.baseCatalogId !== draft.baseCatalogId ||
          entry.baseStamp !== draft.baseStamp,
      ),
      draft,
    ],
  }))
}

export const discardCatalogDraft = (
  client: QueryClient,
  key: QueryKey,
  revision: string,
) => {
  client.setQueryData<CatalogEditorDraftState>(key, (state) => ({
    drafts: (state?.drafts ?? []).filter(
      (draft) => draft.revision !== revision,
    ),
  }))
}

export const completeCatalogDraft = (
  client: QueryClient,
  key: QueryKey,
  revision: string,
  catalogId: string,
) => {
  const state = readCatalogDraftState(client, key)
  if (!state.drafts.some((draft) => draft.revision === revision)) return false
  client.setQueryData<CatalogEditorDraftState>(key, {
    drafts: state.drafts.filter((draft) => draft.revision !== revision),
    lastSaved: { revision, catalogId },
  })
  return true
}
